import { type ChangeEvent, useEffect, useRef, useState } from "react";

import { Button } from "@components/Button";
import { Dialog, DialogContent } from "@components/Dialog";
import { Field, FieldError, FieldLabel } from "@components/Field";
import { Image } from "@components/Image";
import { Input } from "@components/Input";
import { Stack } from "@components/Stack";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { EBroadcastStatus } from "@api/broadcast";
import type { TGoal } from "@api/goals";

import { DIALOG_KEYS, useDialogStore } from "@storage/global/useDialogStore";
import { useBroadcastStatusDataQuery } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import {
	deleteGoalByIdMutation,
	getGoalListQuery,
	startGoalMutation,
	updateGoalQueueMutation,
} from "@storage/queries/goals";

import { cn } from "@lib/utils";

import { ICON_PATHS } from "@configs/icons";

type TEditableGoal = Omit<TGoal, "target_tokens"> & {
	target_tokens: number | null;
};

const AMOUNT_OF_GOALS = 10;

const createEmptyGoal = (): TEditableGoal => ({
	title: "",
	target_tokens: null,
});

const mapGoalToEditable = (goal: TGoal): TEditableGoal => ({
	...goal,
	target_tokens: goal.target_tokens ?? null,
});

const normalizeGoalQueue = (goals: TEditableGoal[]) =>
	goals.map((goal) => ({
		id: goal.id ?? null,
		title: goal.title,
		target_tokens: goal.target_tokens ?? null,
	}));

const areGoalQueuesEqual = (firstGoalQueue: TEditableGoal[], secondGoalQueue: TEditableGoal[]) =>
	JSON.stringify(normalizeGoalQueue(firstGoalQueue)) === JSON.stringify(normalizeGoalQueue(secondGoalQueue));

const createEditableGoalQueue = (goalQueue: TGoal[], activeGoal: TGoal | null): TEditableGoal[] => {
	if (goalQueue.length === 0 && activeGoal === null) {
		return [createEmptyGoal()];
	}

	return goalQueue.map(mapGoalToEditable);
};

const isGoalTitleEmpty = (goal: TEditableGoal) => !goal.title?.trim();
const isGoalTargetEmpty = (goal: TEditableGoal) => goal.target_tokens === null;
const isGoalPartiallyFilled = (goal: TEditableGoal) =>
	(isGoalTitleEmpty(goal) && !isGoalTargetEmpty(goal)) || (!isGoalTitleEmpty(goal) && isGoalTargetEmpty(goal));

const normalizeTargetTokens = (value: string) => {
	const numericValue = value.replace(/\D/g, "");

	if (!numericValue) {
		return null;
	}

	return Math.min(Math.max(Number(numericValue), 1), 99999);
};

interface GoalItemProps {
	goal: TEditableGoal;
	index: number;
	canMoveUp: boolean;
	canMoveDown: boolean;
	handleMoveGoal: (goalIndex: number, direction: "up" | "down") => void;
	handleDeleteGoal: (goal: TEditableGoal, goalIndex: number) => void;
	handleUpdateGoal: (goalIndex: number, updatedFields: Partial<TEditableGoal>) => void;
	isTitleEmpty: boolean;
	isTargetEmpty: boolean;
	isActiveGoal: boolean;
}

const GoalItem = ({
	goal,
	index,
	canMoveUp,
	canMoveDown,
	handleMoveGoal,
	handleDeleteGoal,
	handleUpdateGoal,
	isTitleEmpty,
	isTargetEmpty,
	isActiveGoal,
}: GoalItemProps) => {
	const showRequiredError = isTitleEmpty || isTargetEmpty;
	const titleInputId = `goal-title-${goal.id ?? index}`;
	const targetInputId = `goal-target-${goal.id ?? index}`;

	const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
		handleUpdateGoal(index, { title: event.target.value });
	};

	const handleTargetChange = (event: ChangeEvent<HTMLInputElement>) => {
		handleUpdateGoal(index, { target_tokens: normalizeTargetTokens(event.target.value) });
	};

	return (
		<Stack wide direction="row" spacing={9} key={goal.id ?? `goal-${index}`} className="py-2.25">
			<Stack className="h-8 shrink-0" spacing={4}>
				<button
					type="button"
					className={`flex h-3.25 w-3.5 cursor-pointer items-center justify-center border-none bg-transparent p-0
						disabled:cursor-not-allowed`}
					onClick={() => handleMoveGoal(index, "up")}
					aria-label={`Move goal ${index + 1} up`}
					disabled={!canMoveUp}
				>
					<svg width="7" height="4" viewBox="0 0 7 4" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M6.13212 2.77867C6.29215 2.9387 6.29214 3.19816 6.13211 3.35819C5.97208 3.51822 5.71263 3.51822 5.5526 3.35819L3.12704 0.932637L0.70149 3.35819C0.54146 3.51822 0.282 3.51822 0.12197 3.35819C-0.0380602 3.19816 -0.0380597 2.9387 0.121971 2.77867L2.7407 0.159943C2.95407 -0.0534303 3.30002 -0.0534301 3.51339 0.159943L6.13212 2.77867Z"
							fill={canMoveUp ? "#FFFFFF" : "#8E8793"}
						/>
					</svg>
				</button>

				<button
					type="button"
					className={`flex h-3.25 w-3.5 cursor-pointer items-center justify-center border-none bg-transparent p-0
						disabled:cursor-not-allowed`}
					onClick={() => handleMoveGoal(index, "down")}
					aria-label={`Move goal ${index + 1} down`}
					disabled={!canMoveDown || isActiveGoal}
				>
					<svg width="7" height="4" viewBox="0 0 7 4" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M0.117885 0.699358C-0.042145 0.539328 -0.042145 0.279868 0.117885 0.119838C0.277915 -0.0401918 0.537375 -0.0401921 0.697405 0.119838L3.12296 2.54539L5.54851 0.119838C5.70854 -0.0401918 5.968 -0.0401918 6.12803 0.119838C6.28806 0.279868 6.28806 0.539329 6.12803 0.699359L3.5093 3.31808C3.29593 3.53146 2.94998 3.53146 2.73661 3.31808L0.117885 0.699358Z"
							fill={canMoveDown ? "#FFFFFF" : "#8E8793"}
						/>
					</svg>
				</button>
			</Stack>
			<Field className="flex-1 gap-1">
				<FieldLabel htmlFor={titleInputId} className="sr-only">
					Goal title
				</FieldLabel>
				<Input
					id={titleInputId}
					aria-label={`Goal ${index + 1} title`}
					aria-invalid={isTitleEmpty}
					value={goal.title}
					onChange={handleTitleChange}
					className={cn(
						"h-8 pl-3 text-sm",
						isTitleEmpty && "border-error hover:border-error focus-visible:border-error",
					)}
					maxLength={30}
					disabled={isActiveGoal}
				/>
				{showRequiredError ? <FieldError className="text-xs">This field is required</FieldError> : null}
			</Field>
			<Field className="w-22.5 shrink-0 gap-1">
				<FieldLabel htmlFor={targetInputId} className="sr-only">
					Goal price in tokens
				</FieldLabel>
				<Input
					id={targetInputId}
					aria-label={`Goal ${index + 1} price in tokens`}
					aria-invalid={isTargetEmpty}
					inputMode="numeric"
					pattern="[0-9]*"
					value={goal.target_tokens ?? ""}
					onChange={handleTargetChange}
					className={cn("h-8 text-sm", isTargetEmpty && "border-error hover:border-error focus-visible:border-error")}
					disabled={isActiveGoal}
				/>
			</Field>
			<Tooltip>
				<TooltipTrigger render={<span className="inline-block size-8" />}>
					<Button
						aria-label={`Delete goal ${index + 1}`}
						onClick={() => handleDeleteGoal(goal, index)}
						shape="circle"
						size="auto"
						type="secondary"
						className="size-8 p-0"
						icon={<Image width={26} height={26} src={ICON_PATHS.TRASH} alt="trash" />}
					/>
				</TooltipTrigger>
				<TooltipContent className="bg-[#434343] text-white">Delete</TooltipContent>
			</Tooltip>
		</Stack>
	);
};

interface GoalSettingsDialogProps {
	onClose: () => void;
}

export const GoalSettingsDialog = ({ onClose }: GoalSettingsDialogProps) => {
	const [goalQueue, setGoalQueue] = useState<TEditableGoal[]>([]);
	const [savedGoalQueue, setSavedGoalQueue] = useState<TEditableGoal[]>([]);
	const [hasTriedToSave, setHasTriedToSave] = useState(false);
	const [hasTriedToClose, setHasTriedToClose] = useState(false);
	const [isGoalActionLocked, setIsGoalActionLocked] = useState(false);

	const isGoalActionLockedRef = useRef(false);
	const savedGoalQueueRef = useRef<TEditableGoal[]>([]);
	const { data: goalListData, isSuccess: isSuccessGoalList } = getGoalListQuery();
	const { mutateAsync: updateGoalQueue, isPending: isUpdateGoalQueuePending } = updateGoalQueueMutation();
	const { mutateAsync: startGoal, isPending: isStartGoalPending } = startGoalMutation();
	const { mutate: deleteGoal } = deleteGoalByIdMutation();
	const { broadcastStatus } = useBroadcastStatusDataQuery();
	const { openDialog } = useDialogStore();

	const isAddGoalDisabled =
		(goalQueue.length >= AMOUNT_OF_GOALS && goalListData?.activeGoal === null) ||
		(goalListData?.activeGoal !== null && goalQueue.length + 1 >= AMOUNT_OF_GOALS);

	useEffect(() => {
		if (isSuccessGoalList) {
			const nextGoalQueue = createEditableGoalQueue(goalListData.goalQueue, goalListData.activeGoal);
			const currentSavedGoalQueue = savedGoalQueueRef.current;

			setGoalQueue((currentGoalQueue) =>
				areGoalQueuesEqual(currentGoalQueue, currentSavedGoalQueue) ? nextGoalQueue : currentGoalQueue,
			);
			savedGoalQueueRef.current = nextGoalQueue;
			setSavedGoalQueue(nextGoalQueue);
			setHasTriedToClose(false);
		}
	}, [goalListData, isSuccessGoalList]);

	const hasUnsavedChanges = !areGoalQueuesEqual(goalQueue, savedGoalQueue);
	const isGoalActionPending = isGoalActionLocked || isUpdateGoalQueuePending || isStartGoalPending;

	const lockGoalAction = () => {
		isGoalActionLockedRef.current = true;
		setIsGoalActionLocked(true);
	};

	const unlockGoalAction = () => {
		isGoalActionLockedRef.current = false;
		setIsGoalActionLocked(false);
	};

	// Added extra disabled to prevent multiple clicks
	const runWithGoalActionLock = <T,>(action: () => Promise<T>, fallbackValue: T) => {
		if (isGoalActionLockedRef.current) {
			return Promise.resolve(fallbackValue);
		}
		lockGoalAction();
		return Promise.resolve().then(action).finally(unlockGoalAction);
	};

	const handleAddGoal = () => {
		const newGoal = createEmptyGoal();
		setGoalQueue((prev) => [...prev, newGoal]);
	};

	const handleUpdateGoal = (goalIndex: number, updatedFields: Partial<TEditableGoal>) => {
		setGoalQueue((prev) => prev.map((goal, index) => (index === goalIndex ? { ...goal, ...updatedFields } : goal)));
	};

	const handleMoveGoal = (goalIndex: number, direction: "up" | "down") => {
		setGoalQueue((prev) => {
			const targetIndex = direction === "up" ? goalIndex - 1 : goalIndex + 1;

			if (targetIndex < 0 || targetIndex >= prev.length) {
				return prev;
			}

			const nextGoalList = [...prev];
			[nextGoalList[goalIndex], nextGoalList[targetIndex]] = [nextGoalList[targetIndex], nextGoalList[goalIndex]];

			return nextGoalList;
		});
	};

	const handleDeleteGoal = (goal: TEditableGoal, goalIndex: number) => {
		const activeGoal = goal.status === "in_progress" || goal.status === "away";

		if (activeGoal && hasUnsavedChanges) {
			setHasTriedToClose(true);
			return;
		}

		if (activeGoal) {
			onClose();
			openDialog(DIALOG_KEYS.DELETE_GOAL, {
				goalId: goal.id || "",
				title: goal.title,
			});
			return;
		}

		setGoalQueue((prev) => {
			const nextGoalQueue = prev.filter((_, index) => index !== goalIndex);
			if (nextGoalQueue.length === 0 && goalListData && goalListData.activeGoal !== null) {
				return [];
			}

			return nextGoalQueue.length > 0 ? nextGoalQueue : [createEmptyGoal()];
		});

		if (goal.id) {
			deleteGoal(goal.id || "");
		}
	};

	const saveGoalQueue = async ({ closeOnSuccess = true }: { closeOnSuccess?: boolean } = {}) => {
		setHasTriedToSave(true);
		setHasTriedToClose(false);

		const hasInvalidGoal = goalQueue.some((goal) => isGoalTitleEmpty(goal) || isGoalTargetEmpty(goal));

		if (hasInvalidGoal) {
			return false;
		}

		await updateGoalQueue(
			goalQueue.map((goal) => ({
				...(goal.id ? { id: goal.id } : {}),
				title: goal.title,
				target_tokens: goal.target_tokens as number,
			})),
		);

		const nextSavedGoalQueue = goalQueue.map((goal) => ({ ...goal }));
		savedGoalQueueRef.current = nextSavedGoalQueue;
		setSavedGoalQueue(nextSavedGoalQueue);
		setHasTriedToSave(false);

		if (closeOnSuccess) {
			onClose();
		}

		return true;
	};

	const handleSaveGoalQueue = async ({ closeOnSuccess = true }: { closeOnSuccess?: boolean } = {}) => {
		runWithGoalActionLock(() => saveGoalQueue({ closeOnSuccess }), false);
	};

	const handleStartGoal = async () => {
		runWithGoalActionLock(async () => {
			const isSaved = await saveGoalQueue({ closeOnSuccess: false });

			if (!isSaved) {
				return;
			}

			await startGoal();
			onClose();
		}, undefined);
	};

	const handleCloseDialog = () => {
		const hasPartiallyFilledGoal = goalQueue.some(isGoalPartiallyFilled);

		if (hasPartiallyFilledGoal) {
			setHasTriedToSave(true);
		}

		if (hasUnsavedChanges) {
			setHasTriedToClose(true);
		}

		if (hasPartiallyFilledGoal || hasUnsavedChanges) {
			return;
		}

		onClose();
	};

	const handleDialogOpenChange = (open: boolean) => {
		if (!open) {
			handleCloseDialog();
		}
	};

	return (
		<Dialog open onOpenChange={handleDialogOpenChange}>
			<DialogContent className="w-138.25 max-w-[calc(100vw-32px)] p-5">
				<div className="mb-3.75 flex min-w-0 items-center gap-2.5">
					<Image src={ICON_PATHS.GOAL} alt="Goal" width={24} height={24} />
					<Typography weight="bold" type="span" size="base">
						Goals Settings
					</Typography>
				</div>

				<Stack wide spacing={15}>
					<Typography color="grey-tertiary" size="sm" type="span">
						Encourage viewers to tip by promising a special public show when you reach a set token amount. You can set
						up to 10 goals.
					</Typography>

					<Stack wide>
						<Stack wide align="center" direction="row" justify="between" className="pr-12.5 pl-6">
							<Typography weight="bold" type="span" size="xs">
								Goals
							</Typography>
							<Typography weight="bold" type="span" size="xs">
								Price, Tokens
							</Typography>
						</Stack>
						<Stack wide>
							{isSuccessGoalList && goalListData.activeGoal !== null ? (
								<GoalItem
									key={goalListData.activeGoal.id}
									goal={goalListData.activeGoal}
									index={0}
									canMoveUp={false}
									canMoveDown={false}
									handleMoveGoal={handleMoveGoal}
									handleDeleteGoal={handleDeleteGoal}
									handleUpdateGoal={handleUpdateGoal}
									isTitleEmpty={false}
									isTargetEmpty={false}
									isActiveGoal={true}
								/>
							) : null}

							{goalQueue.map((goal, index) => {
								const isTitleEmpty = hasTriedToSave && isGoalTitleEmpty(goal);
								const isTargetEmpty = hasTriedToSave && isGoalTargetEmpty(goal);
								const canMoveUp = index > 0;
								const canMoveDown = index < goalQueue.length - 1;

								return (
									<GoalItem
										key={goal.id ?? `goal-${index}`}
										goal={goal}
										index={index}
										canMoveUp={canMoveUp}
										canMoveDown={canMoveDown}
										handleMoveGoal={handleMoveGoal}
										handleDeleteGoal={handleDeleteGoal}
										handleUpdateGoal={handleUpdateGoal}
										isTitleEmpty={isTitleEmpty}
										isTargetEmpty={isTargetEmpty}
										isActiveGoal={false}
									/>
								);
							})}
						</Stack>
					</Stack>

					<Stack spacing={12} direction="row" align="center">
						<Button
							aria-label="Add goal"
							onClick={handleAddGoal}
							disable={isAddGoalDisabled}
							shape="circle"
							size="auto"
							type="primary"
							className="size-8 p-0"
							icon={<Image src={ICON_PATHS.PLUS} alt="" width={24} height={24} />}
						/>
						<Typography size="sm" weight="bold" className={cn(isAddGoalDisabled && "cursor-not-allowed opacity-50")}>
							Add more
						</Typography>
					</Stack>

					<Stack wide spacing={8} direction="row">
						<Button wide onClick={() => handleSaveGoalQueue()} disable={isGoalActionPending}>
							<Typography type="span" size="sm" weight="bold">
								Save
							</Typography>
						</Button>
						<Button
							wide
							type={isSuccessGoalList && goalListData.activeGoal !== null ? "secondary" : "success"}
							onClick={handleStartGoal}
							disable={
								(isSuccessGoalList && goalListData.activeGoal !== null) ||
								isGoalActionPending ||
								broadcastStatus === EBroadcastStatus.PRIVATE ||
								broadcastStatus === EBroadcastStatus.OFFLINE
							}
						>
							<Typography type="span" size="sm" weight="bold">
								Start
							</Typography>
						</Button>
					</Stack>
					{hasTriedToClose && hasUnsavedChanges ? (
						<Typography type="span" size="xs" color="error">
							Please save your goals
						</Typography>
					) : null}
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
